import React from "react";
import { useTheme } from "@mui/material";

import {
  Dialog,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  FormControlLabel,
  Checkbox,
} from "@tiny/components";
import { subscribersColumns } from "../SubscribersTable.helpers";
import { SubscriberColumnId } from "../SubscribersTable.types";
import { useColumnsSettingsDialog } from "./ColumnsSettingsDialog.hooks";

export interface ColumnsSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (
    selected: SubscriberColumnId[],
    selectedCustomAttrs: string[]
  ) => void;
  visibleColumns: SubscriberColumnId[];
  customAttributeNames?: string[];
  visibleCustomAttributes?: string[];
}

function ColumnsSettingsDialog(props: ColumnsSettingsDialogProps) {
  const { open, onClose } = props;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      disableBackdropClose
      disableScrollLock
    >
      <ColumnsSettingsDialogContent {...props} />
    </Dialog>
  );
}

function ColumnsSettingsDialogContent(props: ColumnsSettingsDialogProps) {
  const { onClose, customAttributeNames = [] } = props;
  const theme = useTheme();

  const {
    apply,
    isSelected,
    selectColumn,
    allChecked,
    selectAll,
    isDisabled,
    isCustomAttrSelected,
    selectCustomAttr,
  } = useColumnsSettingsDialog(props);

  return (
    <>
      <DialogTitle
        onClose={onClose}
        subtitle="Select the columns to show on the table"
      >
        Columns settings
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={7} verticalSpacing={3}>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={<Checkbox color="primary" />}
              label={"All"}
              checked={allChecked}
              onChange={(_event: React.ChangeEvent<{}>, checked) =>
                selectAll(checked)
              }
            />
          </Grid>
          {subscribersColumns.map((column) => (
            <Grid key={column.id} item xs={12} md={4}>
              <FormControlLabel
                control={<Checkbox color="primary" />}
                label={column.name}
                disabled={isDisabled(column.id)}
                checked={isSelected(column.id)}
                onChange={(_event: React.ChangeEvent<{}>, checked) =>
                  selectColumn(column.id, checked)
                }
              />
            </Grid>
          ))}
          {customAttributeNames.map((name) => (
            <Grid key={name} item xs={12} md={4}>
              <FormControlLabel
                control={<Checkbox color="primary" />}
                label={
                  <span style={{ color: theme.palette.secondary.main }}>
                    {name}
                  </span>
                }
                checked={isCustomAttrSelected(name)}
                onChange={(_event: React.ChangeEvent<{}>, checked) =>
                  selectCustomAttr(name, checked)
                }
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          onClick={apply}
        >
          Apply
        </Button>
      </DialogActions>
    </>
  );
}

export default ColumnsSettingsDialog;
